import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { FiCamera, FiUser } from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import './Profile.css';

const Profile = () => {
  const { t } = useTranslation();
  const { user, updateProfile } = useAuth();
  const [formData, setFormData] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    phone: user?.phone || '',
    bio: user?.bio || '',
    is_host: user?.is_host || false
  });
  const [loading, setLoading] = useState(false);
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(user?.avatar || null);

  const handleChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData({
      ...formData,
      [e.target.name]: value
    });
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setAvatarFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveAvatar = () => {
    setAvatarFile(null);
    setAvatarPreview(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      let avatarUrl = avatarPreview;

      // Upload new avatar if selected
      if (avatarFile) {
        const formDataUpload = new FormData();
        formDataUpload.append('file', avatarFile);

        const uploadResponse = await api.post('/api/uploads/image?folder=avatars', formDataUpload, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });

        avatarUrl = uploadResponse.data.image_url;
      }

      // Update profile with new data
      const updateData = { ...formData };
      if (avatarUrl) {
        updateData.avatar = avatarUrl;
      } else if (avatarPreview === null) {
        // User removed avatar
        updateData.avatar = null;
      }

      const result = await updateProfile(updateData);

      if (result.success) {
        toast.success(t('profile.profileUpdated'));
        setAvatarFile(null); // Clear file after successful upload
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error(t('profile.updateFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="profile-page">
      <div className="container">
        <div className="profile-container">
          <h1>{t('profile.title')}</h1>
          <p className="profile-subtitle">{t('profile.subtitle')}</p>

          <div className="profile-content">
            {/* Profile Form */}
            <div className="profile-form-section">
              <form onSubmit={handleSubmit} className="profile-form">
                {/* Avatar Upload */}
                <div className="form-section" style={{ textAlign: 'center', marginBottom: '2rem', paddingBottom: '2rem', borderBottom: '1px solid #e0e0e0' }}>
                  <h2>{t('profile.profilePhoto')}</h2>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem', marginTop: '1.5rem' }}>
                    <div style={{ position: 'relative', width: '150px', height: '150px' }}>
                      {avatarPreview ? (
                        <img
                          src={avatarPreview}
                          alt="Avatar preview"
                          style={{
                            width: '100%',
                            height: '100%',
                            borderRadius: '50%',
                            objectFit: 'cover',
                            border: '4px solid #e0e0e0'
                          }}
                        />
                      ) : (
                        <div
                          style={{
                            width: '100%',
                            height: '100%',
                            borderRadius: '50%',
                            backgroundColor: '#f0f0f0',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: '4px solid #e0e0e0'
                          }}
                        >
                          <FiUser size={64} color="#999" />
                        </div>
                      )}
                      <label
                        htmlFor="avatar-upload"
                        style={{
                          position: 'absolute',
                          bottom: '0',
                          right: '0',
                          backgroundColor: '#FF385C',
                          borderRadius: '50%',
                          width: '44px',
                          height: '44px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          border: '4px solid white',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                        }}
                      >
                        <FiCamera size={20} color="white" />
                      </label>
                      <input
                        id="avatar-upload"
                        type="file"
                        accept="image/*"
                        onChange={handleAvatarChange}
                        style={{ display: 'none' }}
                      />
                    </div>
                    {avatarPreview && (
                      <button
                        type="button"
                        onClick={handleRemoveAvatar}
                        className="btn btn-secondary btn-sm"
                      >
                        {t('profile.removePhoto')}
                      </button>
                    )}
                    <small style={{ color: '#666', fontSize: '0.875rem' }}>
                      {t('profile.photoHelp')}
                    </small>
                  </div>
                </div>

                <div className="form-section">
                  <h2>{t('profile.personalInfo')}</h2>

                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">{t('profile.firstName')}</label>
                      <input
                        type="text"
                        name="first_name"
                        value={formData.first_name}
                        onChange={handleChange}
                        className="form-input"
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">{t('profile.lastName')}</label>
                      <input
                        type="text"
                        name="last_name"
                        value={formData.last_name}
                        onChange={handleChange}
                        className="form-input"
                        required
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">{t('profile.email')}</label>
                    <input
                      type="email"
                      value={user?.email || ''}
                      className="form-input"
                      disabled
                    />
                    <small className="form-help">{t('profile.emailCannotChange')}</small>
                  </div>

                  <div className="form-group">
                    <label className="form-label">{t('profile.phone')}</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="form-input"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">{t('profile.bio')}</label>
                    <textarea
                      name="bio"
                      value={formData.bio}
                      onChange={handleChange}
                      className="form-textarea"
                      rows="4"
                      placeholder={t('profile.bioPlaceholder')}
                    />
                  </div>
                </div>

                <div className="form-section">
                  <h2>{t('profile.hosting')}</h2>

                  <div className="form-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="is_host"
                        checked={formData.is_host}
                        onChange={handleChange}
                      />
                      <span>{t('profile.wantToHost')}</span>
                    </label>
                  </div>
                </div>

                <button
                  type="submit"
                  className="btn btn-primary btn-lg"
                  disabled={loading}
                >
                  {loading ? t('profile.saving') : t('profile.saveChanges')}
                </button>
              </form>
            </div>

            {/* Account Info Sidebar */}
            <div className="account-sidebar">
              <div className="account-card">
                <h3>{t('profile.accountStatus')}</h3>
                <div className="account-badges">
                  {user?.is_verified && (
                    <span className="badge badge-success">✓ {t('profile.verified')}</span>
                  )}
                  {user?.email_verified && (
                    <span className="badge badge-success">✓ {t('profile.emailVerified')}</span>
                  )}
                  {user?.is_host && (
                    <span className="badge badge-info">{t('profile.host')}</span>
                  )}
                  {user?.is_admin && (
                    <span className="badge badge-danger">{t('profile.admin')}</span>
                  )}
                </div>
              </div>

              <div className="account-card">
                <h3>{t('profile.memberSince')}</h3>
                <p>{user?.created_at ? new Date(user.created_at).toLocaleDateString('pt-BR', { year: 'numeric', month: 'long' }) : 'N/A'}</p>
              </div>

              <div className="account-card">
                <h3>{t('profile.security')}</h3>
                <p className="text-muted mb-2">{t('profile.keepAccountSecure')}</p>
                <button className="btn btn-secondary btn-block">
                  {t('profile.changePassword')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
